import { Fragment } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from '../i18n';

const NAV_GROUPS = [
  {
    key: 'dailyUse',
    items: [
      { to: '/',           icon: '📋', label: 'dashboard' },
      { to: '/upload',     icon: '📤', label: 'upload' },
      { to: '/quick-post', icon: '⚡', label: 'quickPost' },
    ],
  },
  {
    key: 'strategy',
    items: [
      { to: '/calendar',    icon: '📅', label: 'calendar' },
      { to: '/content-map', icon: '🗺',  label: 'contentMap' },
      { to: '/campaigns',   icon: '📢', label: 'campaigns' },
    ],
  },
  {
    key: 'settings',
    items: [
      { to: '/brand',     icon: '🎨', label: 'brand' },
      { to: '/analytics', icon: '📊', label: 'analytics' },
      { to: '/people',    icon: '👥', label: 'people' },
    ],
  },
  {
    key: 'admin',
    items: [
      { to: '/cards',     icon: '💳', label: 'cards', adminOnly: true },
      { to: '/knowledge', icon: '🧠', label: 'knowledge' },
    ],
  },
];

// TODO(auth): replace with the authenticated user from auth context once Cloudflare Access lands.
const CURRENT_USER = { name: 'Rushil', role: 'admin', initials: 'RK' };

// TODO(usage): wire to GET /api/v1/usage once the AI usage tracker endpoint exists.
const AI_USAGE = { used: 657, total: 900 };

function navItemClass({ isActive }) {
  return 'app-sidebar__item' + (isActive ? ' app-sidebar__item--active' : '');
}

export function Sidebar() {
  const { t, language, setLanguage } = useTranslation();
  const usagePercent = Math.round((AI_USAGE.used / AI_USAGE.total) * 100);
  const remaining = Math.max(AI_USAGE.total - AI_USAGE.used, 0);

  return (
    <aside className="app-sidebar">
      <div className="app-sidebar__header">
        <img src="/f8m-logo.svg" alt="" />
        <span>{t('app.name')}</span>
      </div>

      <nav className="app-sidebar__nav" aria-label="Primary">
        {NAV_GROUPS.map((group, groupIndex) => {
          const visibleItems = group.items.filter(
            (item) => !item.adminOnly || CURRENT_USER.role === 'admin',
          );
          if (visibleItems.length === 0) return null;
          return (
            <Fragment key={group.key}>
              {groupIndex > 0 && <div className="app-sidebar__divider" />}
              <div className="app-sidebar__group-label">
                {t(`sidebar.groups.${group.key}`)}
              </div>
              {visibleItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={navItemClass}
                >
                  <span className="app-sidebar__icon" aria-hidden="true">{item.icon}</span>
                  <span className="app-sidebar__label">{t(`sidebar.${item.label}`)}</span>
                  {item.adminOnly && (
                    <span className="app-sidebar__badge">{t('sidebar.adminBadge')}</span>
                  )}
                </NavLink>
              ))}
            </Fragment>
          );
        })}
      </nav>

      <div className="app-sidebar__footer">
        <div className="app-sidebar__usage" aria-label={t('usage.aiUsage')}>
          <div className="app-sidebar__usage-label">
            <span>{t('usage.aiUsage')}</span>
            <span>{t('usage.percentage', { percent: usagePercent })}</span>
          </div>
          <div className="app-sidebar__usage-bar">
            <div
              className="app-sidebar__usage-fill"
              style={{ width: `${usagePercent}%` }}
            />
          </div>
          <div style={{ marginTop: 4 }}>
            {t('usage.remaining', { count: remaining })}
          </div>
        </div>

        <div className="app-sidebar__user">
          <div className="app-sidebar__user-avatar">{CURRENT_USER.initials}</div>
          <div>
            <div className="app-sidebar__user-name">{CURRENT_USER.name}</div>
            <div className="app-sidebar__user-role">{CURRENT_USER.role}</div>
          </div>
        </div>

        <div className="app-sidebar__lang" role="group" aria-label="Language">
          <button
            type="button"
            aria-pressed={language === 'en'}
            onClick={() => setLanguage('en')}
          >
            EN 🇬🇧
          </button>
          <button
            type="button"
            aria-pressed={language === 'pl'}
            onClick={() => setLanguage('pl')}
          >
            PL 🇵🇱
          </button>
        </div>
      </div>
    </aside>
  );
}
